import type {
	PostErrorFullRegisterResponse,
	PostErrorResponse,
	PostRegisterErrorResponse,
} from 'services/idm';

/**
 * IDM returns field validation messages keyed by the PascalCase API field name
 * (`Email`, `Address.Postcode`, ...) with an array of messages per field.
 */
export type IdmApiFieldErrors = Record<string, string[] | undefined>;

export type IdmFormErrorState = {
	/** Message for the page-level error block, already translated. */
	banner: string;
	/** Per-field API messages, keyed by the PascalCase API field name. */
	fields: IdmApiFieldErrors;
};

export type IdmApiError =
	| PostErrorResponse
	| PostErrorFullRegisterResponse
	| PostRegisterErrorResponse;

/** Translation keys that differ between the register-style forms. */
export type IdmErrorMessageKeys = {
	usedEmail?: string;
	usedMobile?: string;
	unknownServer?: string;
};

type Translate = (
	key: string,
	values?: Record<string, string>,
	fallback?: string
) => string;

export const EMPTY_IDM_FORM_ERRORS: IdmFormErrorState = {
	banner: '',
	fields: {},
};

const DEFAULT_UNKNOWN_SERVER_KEY = 'common.500_error_message';

/** IDM sends several messages per field; the forms render them as one block. */
export const joinApiMessages = (messages?: string[]): string =>
	messages?.join('<br>') ?? '';

const isValidationError = (error: IdmApiError, status: number) =>
	error.title === 'VALIDATION_ERROR' && error.status === status;

/**
 * Collapses an IDM error response into the banner/field split that every
 * account form renders. Previously duplicated across signin, register,
 * full-register and required-info with slightly different message keys.
 *
 * Placeholders such as `{signInUrl}` are substituted by `useAppTranslation`,
 * which already receives the page's `placeholders` object.
 */
export function mapIdmApiError(
	error: IdmApiError,
	t: Translate,
	messageKeys: IdmErrorMessageKeys = {}
): IdmFormErrorState {
	if (isValidationError(error, 400)) {
		return { banner: error.detail ?? '', fields: {} };
	}

	if (isValidationError(error, 422)) {
		return {
			banner: '',
			fields: (error.errors as IdmApiFieldErrors) ?? {},
		};
	}

	if (error.title === 'USED_EMAIL_ERROR' && messageKeys.usedEmail) {
		return { banner: t(messageKeys.usedEmail), fields: {} };
	}

	if (error.title === 'USED_MOBILE_ERROR' && messageKeys.usedMobile) {
		return { banner: t(messageKeys.usedMobile), fields: {} };
	}

	return {
		banner: t(messageKeys.unknownServer ?? DEFAULT_UNKNOWN_SERVER_KEY),
		fields: {},
	};
}
